import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Modal,
  Divider,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';

import * as XLSX from 'xlsx';

import { db, auth } from '../firebase';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';

import SignInImg from '../img/SignInImg.png';

const WEEKDAYS = [
  { label: 'Monday', dayOfWeek: 1 },
  { label: 'Tuesday', dayOfWeek: 2 },
  { label: 'Wednesday', dayOfWeek: 3 },
  { label: 'Thursday', dayOfWeek: 4 },
  { label: 'Friday', dayOfWeek: 5 },
  { label: 'Saturday', dayOfWeek: 6 },
];

const LAB_OPTIONS = ['NEXUS', 'RAISE LAB', 'SAND BOX'];

const createEmptyFormData = () => ({
  selectedLab: '',
  facultyDocId: '',
  course: '',
  yearLevel: '',
  section: '',
  subject: '',
  startTime: '',
  endTime: '',
  active: true,
  graceMinutesBefore: 15,
  graceMinutesAfter: 10,
  term: 'Second Semester, 2025-2026',
});

const getErrorMessage = (error, fallback = 'Unknown error') =>
  error?.message || error?.details || fallback;

const normalizeText = (value) => String(value || '').trim();

const normalizeLower = (value) => normalizeText(value).toLowerCase();

const buildFacultyName = (faculty = {}) => {
  const lastName = normalizeText(faculty.lastName);
  const firstName = normalizeText(faculty.firstName);
  const middleName = normalizeText(faculty.middleName);

  const firstMiddle = [firstName, middleName].filter(Boolean).join(' ');

  if (lastName && firstMiddle) return `${lastName}, ${firstMiddle}`;
  if (lastName) return lastName;
  return firstMiddle || faculty.name || faculty.fullName || 'Professor';
};

const buildFacultyNameForward = (faculty = {}) => {
  const firstName = normalizeText(faculty.firstName);
  const middleName = normalizeText(faculty.middleName);
  const lastName = normalizeText(faculty.lastName);

  return [firstName, middleName, lastName].filter(Boolean).join(' ');
};

const timeToMinutes = (timeValue) => {
  if (!timeValue) return 0;

  const clean = String(timeValue).trim();

  if (clean.includes(':')) {
    const [hours, minutes] = clean.split(':').map(Number);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;

    return hours * 60 + minutes;
  }

  return 0;
};

const formatTime12Hour = (timeValue) => {
  if (!timeValue) return '-';

  const [hoursRaw, minutesRaw] = String(timeValue).split(':').map(Number);

  if (Number.isNaN(hoursRaw) || Number.isNaN(minutesRaw)) return timeValue;

  const period = hoursRaw >= 12 ? 'PM' : 'AM';
  const hours = hoursRaw % 12 || 12;
  const minutes = String(minutesRaw).padStart(2, '0');

  return `${hours}:${minutes} ${period}`;
};

const formatScheduleTime = (startTime, endTime) => {
  if (!startTime || !endTime) return '-';
  return `${formatTime12Hour(startTime)} - ${formatTime12Hour(endTime)}`;
};

const getSubjectValue = (subject) =>
  normalizeText(subject?.code || subject?.description || subject?.subject || '');

const getSubjectLabel = (subject) => {
  const code = normalizeText(subject?.code);
  const description = normalizeText(subject?.description || subject?.subject);

  if (code && description) return `${code} - ${description}`;
  return code || description || 'Subject';
};

const normalizeHeaderKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

const getCellValue = (row, possibleKeys) => {
  const normalizedEntries = Object.entries(row || {}).map(([key, value]) => [
    normalizeHeaderKey(key),
    value,
  ]);

  for (const rawKey of possibleKeys) {
    const normalizedKey = normalizeHeaderKey(rawKey);
    const found = normalizedEntries.find(([key]) => key === normalizedKey);
    if (found) return found[1];
  }

  return '';
};

const getDayFromName = (dayName) => {
  const clean = normalizeLower(dayName);
  const found = WEEKDAYS.find((day) => normalizeLower(day.label) === clean);
  return found || null;
};

const parseActiveValue = (value) => {
  const clean = normalizeLower(value);

  if (value === true) return true;
  if (value === false) return false;

  if (['true', 'active', 'yes', '1'].includes(clean)) return true;
  if (['false', 'inactive', 'no', '0'].includes(clean)) return false;

  return true;
};

const Semester = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState(createEmptyFormData());

  const [courses, setCourses] = useState([]);
  const [yearLevels, setYearLevels] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [faculties, setFaculties] = useState([]);
  const [schedules, setSchedules] = useState([]);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [tabValue, setTabValue] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(7);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const currentDay = WEEKDAYS[tabValue];

  useEffect(() => {
    if (!auth.currentUser) {
      setError('You must be logged in to access this page.');
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setInitialLoading(true);
        setError('');

        const [settingsDoc, facultySnapshot, scheduleSnapshot] =
          await Promise.all([
            getDoc(doc(db, 'Settings', 'global')),
            getDocs(collection(db, 'Faculties')),
            getDocs(collection(db, 'Schedules')),
          ]);

        if (settingsDoc.exists()) {
          const data = settingsDoc.data();

          setCourses(data.courses || []);
          setYearLevels(data.yearLevels || []);
          setSections(data.sections || []);
          setSubjects(data.subjects || []);

          if (!data.courses) {
            setError('No courses found in Settings/global.');
          }
        } else {
          setError('Configuration data not found.');
        }

        const facultyList = facultySnapshot.docs
          .map((docItem) => {
            const data = docItem.data();

            return {
              id: docItem.id,
              ...data,
              name: buildFacultyName(data),
              forwardName: buildFacultyNameForward(data),
              userRole: String(data.userRole || 'Faculty').trim(),
            };
          })
          .filter(
            (faculty) =>
              normalizeLower(faculty.userRole || faculty.role) === 'faculty'
          )
          .sort((a, b) => a.name.localeCompare(b.name));

        const scheduleList = scheduleSnapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));

        setFaculties(facultyList);
        setSchedules(scheduleList);
      } catch (err) {
        setError('Failed to fetch semester data: ' + getErrorMessage(err));
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [error, success]);

  const refreshSchedules = async () => {
    const snapshot = await getDocs(collection(db, 'Schedules'));

    setSchedules(
      snapshot.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
      }))
    );
  };

  const resetForm = () => {
    setFormData(createEmptyFormData());
    setEditMode(false);
    setEditId(null);
  };

  const openAddModal = () => {
    setEditMode(false);
    setEditId(null);
    setFormData(createEmptyFormData());
    setModalOpen(true);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setModalOpen(false);
    resetForm();
    setPage(0);
    setSearchTerm('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'course') {
      setFormData((prev) => ({
        ...prev,
        course: value,
        subject: '',
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEdit = (id) => {
    const data = schedules.find((schedule) => schedule.id === id);

    if (!data) {
      setError('Failed to load schedule data for editing.');
      return;
    }

    setFormData({
      selectedLab: data.selectedLab || '',
      facultyDocId: data.facultyDocId || '',
      course: data.course || '',
      yearLevel: data.yearLevel || '',
      section: data.section || '',
      subject: data.subject || '',
      startTime: data.startTime || '',
      endTime: data.endTime || '',
      active: data.active !== false,
      graceMinutesBefore:
        data.graceMinutesBefore === undefined ? 15 : data.graceMinutesBefore,
      graceMinutesAfter:
        data.graceMinutesAfter === undefined ? 10 : data.graceMinutesAfter,
      term: data.term || 'Second Semester, 2025-2026',
    });

    setEditMode(true);
    setEditId(id);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const docRef = doc(db, 'Schedules', editId);
      await deleteDoc(docRef);

      setSuccess('Schedule removed successfully!');

      await refreshSchedules();
      setPage(0);
      setDeleteOpen(false);
      setModalOpen(false);
      resetForm();
    } catch (err) {
      setError('Failed to delete schedule: ' + getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleImportButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const buildFacultyLookup = () => {
    const facultyLookup = new Map();

    faculties.forEach((faculty) => {
      const possibleKeys = [
        faculty.id,
        faculty.facultyId,
        faculty.rfid,
        faculty.email,
        faculty.name,
        faculty.forwardName,
        `${faculty.firstName || ''} ${faculty.lastName || ''}`,
        `${faculty.firstName || ''} ${faculty.middleName || ''} ${faculty.lastName || ''}`,
        `${faculty.lastName || ''}, ${faculty.firstName || ''}`,
        `${faculty.lastName || ''}, ${faculty.firstName || ''} ${faculty.middleName || ''}`,
      ];

      possibleKeys
        .filter(Boolean)
        .map((value) => normalizeLower(value))
        .filter(Boolean)
        .forEach((key) => {
          facultyLookup.set(key, faculty);
        });
    });

    return facultyLookup;
  };

  const mapScheduleImportRow = (row, index, facultyLookup) => {
    const selectedLab = normalizeText(
      getCellValue(row, ['selectedLab', 'selected lab', 'laboratory', 'lab'])
    );

    const dayNameRaw = normalizeText(
      getCellValue(row, ['dayName', 'day name', 'day'])
    );

    const dayOfWeekRaw = getCellValue(row, [
      'dayOfWeek',
      'day of week',
      'day number',
    ]);

    const startTime = normalizeText(
      getCellValue(row, ['startTime', 'start time', 'start'])
    );

    const endTime = normalizeText(
      getCellValue(row, ['endTime', 'end time', 'end'])
    );

    const subject = normalizeText(
      getCellValue(row, ['subject', 'subject code', 'code'])
    );

    const originalClass = normalizeText(
      getCellValue(row, ['originalClass', 'original class', 'class'])
    );

    const course = normalizeText(getCellValue(row, ['course', 'program']));

    const yearLevel = normalizeText(
      getCellValue(row, ['yearLevel', 'year level', 'year'])
    );

    const section = normalizeText(getCellValue(row, ['section']));

    const facultyName = normalizeText(
      getCellValue(row, [
        'facultyName',
        'faculty name',
        'professor',
        'teacher',
        'instructor',
      ])
    );

    const facultyDocId = normalizeText(
      getCellValue(row, [
        'facultyDocId',
        'faculty doc id',
        'faculty document id',
      ])
    );

    const facultyId = normalizeText(
      getCellValue(row, ['facultyId', 'faculty id', 'faculty number'])
    );

    const facultyRfid = normalizeText(
      getCellValue(row, ['facultyRfid', 'faculty rfid', 'rfid'])
    );

    const active = parseActiveValue(getCellValue(row, ['active', 'status']));

    const graceMinutesBefore = Number(
      getCellValue(row, [
        'graceMinutesBefore',
        'grace before',
        'grace before minutes',
      ]) || 15
    );

    const graceMinutesAfter = Number(
      getCellValue(row, [
        'graceMinutesAfter',
        'grace after',
        'grace after minutes',
      ]) || 10
    );

    const term =
      normalizeText(getCellValue(row, ['term', 'semester'])) ||
      'Second Semester, 2025-2026';

    const sourceNotes = normalizeText(
      getCellValue(row, ['sourceNotes', 'source notes', 'notes'])
    );

    const dayFromName = getDayFromName(dayNameRaw);
    const dayOfWeek = Number(dayOfWeekRaw || dayFromName?.dayOfWeek || 0);
    const dayName = dayFromName?.label || dayNameRaw;

    const startMinute =
      Number(getCellValue(row, ['startMinute', 'start minute'])) ||
      timeToMinutes(startTime);

    const endMinute =
      Number(getCellValue(row, ['endMinute', 'end minute'])) ||
      timeToMinutes(endTime);

    const facultyMatch =
      facultyLookup.get(normalizeLower(facultyDocId)) ||
      facultyLookup.get(normalizeLower(facultyId)) ||
      facultyLookup.get(normalizeLower(facultyRfid)) ||
      facultyLookup.get(normalizeLower(facultyName)) ||
      null;

    const missingRequired =
      !selectedLab ||
      !dayName ||
      !dayOfWeek ||
      !startTime ||
      !endTime ||
      !subject ||
      !course ||
      !yearLevel ||
      !section ||
      !facultyName;

    if (missingRequired) {
      return {
        valid: false,
        rowNumber: index + 2,
        reason: 'Missing required field.',
      };
    }

    if (endMinute <= startMinute) {
      return {
        valid: false,
        rowNumber: index + 2,
        reason: 'End time must be later than start time.',
      };
    }

    return {
      valid: true,
      data: {
        active,

        dayOfWeek,
        dayName,

        startTime,
        endTime,
        startMinute,
        endMinute,

        selectedLab,

        facultyDocId: facultyMatch?.id || facultyDocId,
        facultyId: facultyMatch?.facultyId || facultyId,
        facultyRfid: facultyMatch?.rfid || facultyRfid,
        facultyName: facultyMatch?.name || facultyName,

        course,
        yearLevel,
        section,
        subject,
        originalClass,

        graceMinutesBefore,
        graceMinutesAfter,
        term,
        sourceNotes,

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
    };
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setError('');
    setSuccess('');

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });

      const worksheet =
        workbook.Sheets['Schedule_Import'] ||
        workbook.Sheets[workbook.SheetNames?.[0]];

      if (!worksheet) {
        throw new Error('No worksheet found in the selected file.');
      }

      const rows = XLSX.utils.sheet_to_json(worksheet, {
        defval: '',
        raw: false,
      });

      if (!rows.length) {
        throw new Error('The selected schedule file is empty.');
      }

      const facultyLookup = buildFacultyLookup();

      const validSchedules = [];
      const skippedRows = [];

      rows.forEach((row, index) => {
        const mapped = mapScheduleImportRow(row, index, facultyLookup);

        if (mapped.valid) {
          validSchedules.push(mapped.data);
        } else {
          skippedRows.push(mapped);
        }
      });

      if (!validSchedules.length) {
        throw new Error(
          `No valid schedule rows found. ${skippedRows.length} row(s) were skipped.`
        );
      }

      await Promise.all(
        validSchedules.map((schedule) =>
          addDoc(collection(db, 'Schedules'), schedule)
        )
      );

      await refreshSchedules();
      setPage(0);

      if (skippedRows.length > 0) {
        console.warn('Skipped schedule rows:', skippedRows);
        setSuccess(
          `${validSchedules.length} schedule row(s) imported. ${skippedRows.length} row(s) skipped.`
        );
      } else {
        setSuccess(
          `${validSchedules.length} schedule row(s) imported successfully!`
        );
      }
    } catch (err) {
      setError('Failed to import schedules: ' + getErrorMessage(err));
    } finally {
      setImporting(false);
    }
  };

  const filteredSubjects = useMemo(() => {
    if (!formData.course) return subjects;

    const courseValue = normalizeLower(formData.course);

    const matched = subjects.filter((subject) => {
      const subjectCourse = normalizeLower(subject?.course || subject?.program);
      return !subjectCourse || subjectCourse === courseValue;
    });

    return matched.length ? matched : subjects;
  }, [subjects, formData.course]);

  const filteredSchedules = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();

    const dayOnly = schedules.filter(
      (schedule) => Number(schedule.dayOfWeek) === currentDay.dayOfWeek
    );

    const filtered = !keyword
      ? dayOnly
      : dayOnly.filter((schedule) => {
          const searchableText = [
            schedule.startTime,
            schedule.endTime,
            schedule.selectedLab,
            schedule.facultyName,
            schedule.course,
            schedule.yearLevel,
            schedule.section,
            schedule.subject,
            schedule.term,
            schedule.active === false ? 'inactive' : 'active',
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          return searchableText.includes(keyword);
        });

    return [...filtered].sort((a, b) => {
      const aTime = Number(a.startMinute || 0);
      const bTime = Number(b.startMinute || 0);

      return aTime - bTime;
    });
  }, [schedules, searchTerm, currentDay.dayOfWeek]);

  const paginatedSchedules = filteredSchedules.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const validateSchedule = () => {
    const requiredFields = [
      'selectedLab',
      'facultyDocId',
      'course',
      'yearLevel',
      'section',
      'subject',
      'startTime',
      'endTime',
    ];

    if (requiredFields.some((field) => !String(formData[field] || '').trim())) {
      return 'Please fill in all required fields.';
    }

    const startMinute = timeToMinutes(formData.startTime);
    const endMinute = timeToMinutes(formData.endTime);

    if (endMinute <= startMinute) {
      return 'End time must be later than start time.';
    }

    const selectedFaculty = faculties.find(
      (faculty) => faculty.id === formData.facultyDocId
    );

    if (!selectedFaculty) {
      return 'Selected faculty was not found.';
    }

    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError('');
    setSuccess('');

    const validationMessage = validateSchedule();

    if (validationMessage) {
      setError(validationMessage);
      setLoading(false);
      return;
    }

    const startMinute = timeToMinutes(formData.startTime);
    const endMinute = timeToMinutes(formData.endTime);

    const selectedFaculty = faculties.find(
      (faculty) => faculty.id === formData.facultyDocId
    );

    try {
      const scheduleData = {
        active: Boolean(formData.active),

        dayOfWeek: currentDay.dayOfWeek,
        dayName: currentDay.label,

        startTime: formData.startTime,
        endTime: formData.endTime,
        startMinute,
        endMinute,

        selectedLab: formData.selectedLab,

        facultyDocId: selectedFaculty.id,
        facultyId: selectedFaculty.facultyId || '',
        facultyRfid: selectedFaculty.rfid || '',
        facultyName: selectedFaculty.name,

        course: formData.course,
        yearLevel: formData.yearLevel,
        section: formData.section,
        subject: formData.subject,

        graceMinutesBefore: Number(formData.graceMinutesBefore || 0),
        graceMinutesAfter: Number(formData.graceMinutesAfter || 0),
        term: formData.term || 'Second Semester, 2025-2026',

        updatedAt: serverTimestamp(),
      };

      if (editMode) {
        await updateDoc(doc(db, 'Schedules', editId), scheduleData);
        setSuccess('Schedule updated successfully!');
      } else {
        await addDoc(collection(db, 'Schedules'), {
          ...scheduleData,
          createdAt: serverTimestamp(),
        });
        setSuccess('Schedule added successfully!');
      }

      resetForm();
      setModalOpen(false);
      await refreshSchedules();
      setPage(0);
    } catch (err) {
      setError(
        `Failed to ${editMode ? 'update' : 'add'} schedule: ${getErrorMessage(
          err
        )}`
      );
    } finally {
      setLoading(false);
    }
  };

  const baseInputStyles = {
    fontFamily: 'Poppins, sans-serif',
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      fontSize: '0.9rem',
      height: '40px',
      padding: '0 14px',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box',
      '&:hover': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#130f72',
        },
      },
      '&.Mui-focused': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#130f72',
        },
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.9rem',
      fontFamily: 'Poppins, sans-serif',
      color: '#130f72 !important',
      transform: 'translate(14px, 10px) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)',
        color: '#130f72 !important',
      },
      '&.Mui-focused': {
        color: '#130f72 !important',
      },
    },
    '& .MuiOutlinedInput-input': {
      boxSizing: 'border-box',
      textAlign: 'left',
      fontFamily: 'Poppins, sans-serif',
      '&::placeholder': {
        color: 'grey.400',
        opacity: 1,
      },
    },
    '& .MuiSelect-select': {
      padding: '1px 14px 10px 4px',
      paddingBottom: '10px',
      height: '40px !important',
      lineHeight: '40px',
      boxSizing: 'border-box',
      textAlign: 'left',
      fontFamily: 'Poppins, sans-serif',
    },
    '& .MuiInputBase-input::placeholder': {
      color: 'grey.400',
      opacity: 1,
    },
  };

  const scheduleFieldStyles = {
    ...baseInputStyles,
    width: '250px',
    maxWidth: '250px',
    '& .MuiOutlinedInput-input': {
      ...baseInputStyles['& .MuiOutlinedInput-input'],
      padding: '10px 14px 10px 4px',
      width: '250px',
    },
    '& .MuiSelect-select': {
      ...baseInputStyles['& .MuiSelect-select'],
      width: '250px',
      maxWidth: '250px',
    },
  };

  const searchFieldStyles = {
    width: '320px',
    '& .MuiOutlinedInput-root': {
      height: '42px',
      borderRadius: '12px',
      fontFamily: 'Poppins, sans-serif',
      backgroundColor: '#ffffff',
      transition: 'all 0.3s ease',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(0, 0, 0, 0.23)',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#130f72',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#130f72',
      },
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Poppins, sans-serif',
      '&::placeholder': {
        color: 'grey.400',
        opacity: 1,
      },
    },
  };

  const addButtonStyles = {
    py: 1,
    borderRadius: '12px',
    boxShadow: 'none',
    backgroundColor: '#130f72 !important',
    color: '#ffc400',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 700,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#0f0c5c !important',
      boxShadow: '0 2px 8px rgba(19, 15, 114, 0.2)',
    },
    '&:active': {
      backgroundColor: '#0b0947 !important',
    },
  };

  const importIconButtonStyles = {
    width: 42,
    height: 42,
    borderRadius: '12px',
    backgroundColor: '#130f72',
    color: '#ffc400',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#0f0c5c',
      boxShadow: '0 2px 8px rgba(19, 15, 114, 0.2)',
    },
    '&:active': {
      backgroundColor: '#0b0947',
    },
    '&.Mui-disabled': {
      backgroundColor: '#130f72',
      color: '#ffc400',
      opacity: 0.65,
    },
  };

  const tooltipSlotProps = {
    tooltip: {
      sx: {
        bgcolor: '#130f72',
        color: '#ffc400',
        fontFamily: 'Poppins, sans-serif',
        fontSize: '0.82rem',
        fontWeight: 600,
        borderRadius: '10px',
        boxShadow: '0 6px 18px rgba(19, 15, 114, 0.25)',
        px: 1.25,
        py: 0.8,
      },
    },
    arrow: {
      sx: {
        color: '#130f72',
      },
    },
  };

  const singleLineAlertSx = {
    borderRadius: '12px',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 700,
    boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
    color: '#fff',
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    '& .MuiAlert-message': {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      display: 'block',
      minWidth: 0,
    },
    '& .MuiAlert-icon': {
      color: '#fff',
      flexShrink: 0,
    },
    '& .MuiAlert-action': {
      alignItems: 'center',
    },
  };

  const renderEmptyStateRow = (message, colSpan) => (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        align="center"
        sx={{ py: 7, borderBottom: 'none' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: 'rgba(19, 15, 114, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <InboxRoundedIcon
              sx={{
                fontSize: '2.2rem',
                color: '#130f72',
              }}
            />
          </Box>

          <Typography
            sx={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontWeight: 700,
              color: '#130f72',
              fontSize: '1.05rem',
            }}
          >
            No records found
          </Typography>

          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              color: 'grey.400',
              fontSize: '0.92rem',
            }}
          >
            {message}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  const renderForm = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          mb: 4,
          px: { xs: 1, md: 4 },
          boxSizing: 'border-box',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: 'Space Grotesk, sans-serif',
            color: '#130f72',
            fontWeight: 700,
          }}
        >
          {editMode ? 'Edit Schedule' : `Add Schedule - ${currentDay.label}`}
        </Typography>

        {editMode && (
          <Button
            variant="contained"
            color="inherit"
            startIcon={<DeleteIcon sx={{ fontSize: '1rem', color: '#c53030' }} />}
            onClick={() => setDeleteOpen(true)}
            disabled={loading}
            sx={{
              py: 0.5,
              px: 2,
              borderRadius: '7px',
              boxShadow: 'none',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '0.7rem',
              backgroundColor: '#fed7d7 !important',
              color: '#000000',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#f4c7c7 !important',
                boxShadow: '0 2px 8px rgba(244, 199, 199, 0.2)',
              },
              '&:active': {
                backgroundColor: '#e6b7b7 !important',
              },
            }}
          >
            Remove Schedule
          </Button>
        )}
      </Box>

      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <Grid container spacing={3} justifyContent="center">
          <Grid container item xs={12} spacing={3} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <TextField
                select
                label="Laboratory"
                InputLabelProps={{ required: false }}
                name="selectedLab"
                value={formData.selectedLab}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                sx={scheduleFieldStyles}
              >
                {LAB_OPTIONS.map((lab) => (
                  <MenuItem key={lab} value={lab}>
                    {lab}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <TextField
                select
                label="Faculty"
                InputLabelProps={{ required: false }}
                name="facultyDocId"
                value={formData.facultyDocId}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                sx={scheduleFieldStyles}
              >
                {faculties.map((faculty) => (
                  <MenuItem key={faculty.id} value={faculty.id}>
                    {faculty.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <TextField
                select
                label="Course"
                InputLabelProps={{ required: false }}
                name="course"
                value={formData.course}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                sx={scheduleFieldStyles}
              >
                {courses.map((course) => (
                  <MenuItem key={course} value={course}>
                    {course}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Grid container item xs={12} spacing={3} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <TextField
                select
                label="Year Level"
                InputLabelProps={{ required: false }}
                name="yearLevel"
                value={formData.yearLevel}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                sx={scheduleFieldStyles}
              >
                {yearLevels.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <TextField
                select
                label="Section"
                InputLabelProps={{ required: false }}
                name="section"
                value={formData.section}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                sx={scheduleFieldStyles}
              >
                {sections.map((section) => (
                  <MenuItem key={section} value={section}>
                    {section}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <TextField
                select
                label="Subject"
                InputLabelProps={{ required: false }}
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                sx={scheduleFieldStyles}
              >
                {filteredSubjects.map((subject) => {
                  const value = getSubjectValue(subject);
                  const label = getSubjectLabel(subject);

                  return (
                    <MenuItem key={value || label} value={value || label}>
                      {label}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
          </Grid>

          <Grid container item xs={12} spacing={3} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <TextField
                label="Start Time"
                InputLabelProps={{ required: false, shrink: true }}
                name="startTime"
                type="time"
                value={formData.startTime}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                sx={scheduleFieldStyles}
              />
            </Grid>

            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <TextField
                label="End Time"
                InputLabelProps={{ required: false, shrink: true }}
                name="endTime"
                type="time"
                value={formData.endTime}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                sx={scheduleFieldStyles}
              />
            </Grid>

            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <TextField
                select
                label="Status"
                InputLabelProps={{ required: false }}
                name="active"
                value={formData.active ? 'active' : 'inactive'}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    active: e.target.value === 'active',
                  }))
                }
                fullWidth
                variant="outlined"
                sx={scheduleFieldStyles}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Grid container item xs={12} spacing={3} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <TextField
                label="Grace Before"
                placeholder="15"
                InputLabelProps={{ required: false }}
                name="graceMinutesBefore"
                value={formData.graceMinutesBefore}
                onChange={handleChange}
                fullWidth
                type="number"
                variant="outlined"
                sx={scheduleFieldStyles}
              />
            </Grid>

            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <TextField
                label="Grace After"
                placeholder="10"
                InputLabelProps={{ required: false }}
                name="graceMinutesAfter"
                value={formData.graceMinutesAfter}
                onChange={handleChange}
                fullWidth
                type="number"
                variant="outlined"
                sx={scheduleFieldStyles}
              />
            </Grid>

            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <TextField
                label="Term"
                placeholder="Second Semester, 2025-2026"
                InputLabelProps={{ required: false }}
                name="term"
                value={formData.term}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={scheduleFieldStyles}
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2, backgroundColor: 'rgba(79, 209, 197, 0.2)' }} />
          </Grid>
        </Grid>

        <Grid container justifyContent="flex-end" spacing={2} sx={{ px: { xs: 2, md: 6.5 }, mt: 3 }}>
          <Grid item xs={12} sm={3}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setModalOpen(false);
                resetForm();
              }}
              disabled={loading}
              sx={{
                py: 1,
                borderRadius: '12px',
                boxShadow: 'none',
                transition: 'all 0.3s ease',
                borderColor: '#130f72',
                color: '#130f72',
                fontFamily: 'Poppins, sans-serif',
                '&:hover': {
                  boxShadow: 'none',
                  backgroundColor: 'rgba(19, 15, 114, 0.06)',
                  borderColor: '#130f72',
                },
              }}
            >
              Cancel
            </Button>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Button
              type="submit"
              variant="contained"
              color="inherit"
              fullWidth
              disabled={loading}
              sx={{
                py: 1,
                borderRadius: '12px',
                boxShadow: 'none',
                backgroundColor: '#130f72 !important',
                color: '#ffc400',
                fontFamily: 'Poppins, sans-serif',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#0f0c5c !important',
                  boxShadow: '0 2px 8px rgba(19, 15, 114, 0.2)',
                },
                '&:active': {
                  backgroundColor: '#0b0947 !important',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: '#ffc400' }} />
              ) : editMode ? (
                'Update Schedule'
              ) : (
                'Add Schedule'
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );

  if (initialLoading) {
    return (
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#130f72', mb: 2 }} />
          <Typography sx={{ color: '#130f72', fontWeight: 700 }}>
            Loading semester...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleImportFile}
      />

      <Box
        sx={{
          position: 'fixed',
          top: 45,
          right: 55,
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: '300px',
          maxWidth: '300px',
        }}
      >
        {error && (
          <Alert
            severity="error"
            variant="filled"
            sx={{
              ...singleLineAlertSx,
              bgcolor: '#b90d0d',
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            variant="filled"
            sx={{
              ...singleLineAlertSx,
              bgcolor: '#0f56b3',
            }}
          >
            {success}
          </Alert>
        )}
      </Box>

      <Box>
        <Paper
          sx={{
            p: 3,
            m: 5,
            mt: '33px',
            pl: '40px',
            borderRadius: '15px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', md: 'center' },
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              mb: 2,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="Semester schedule tabs"
              sx={{
                fontFamily: 'Poppins, sans-serif',
                '& .MuiTab-root': {
                  color: 'grey.400',
                  fontFamily: 'Poppins, sans-serif',
                  '&.Mui-selected': {
                    color: '#130f72',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(19, 15, 114, 0.08)',
                    borderRadius: '15px',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#130f72',
                },
              }}
            >
              {WEEKDAYS.map((day) => (
                <Tab key={day.label} label={day.label} />
              ))}
            </Tabs>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
              }}
            >
              <TextField
                placeholder={`Search ${currentDay.label} schedule`}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                sx={searchFieldStyles}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon
                        sx={{
                          color: 'grey.400',
                          '.Mui-focused &': {
                            color: '#130f72',
                          },
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              <Tooltip title="Import Schedule" arrow slotProps={tooltipSlotProps}>
                <span>
                  <IconButton
                    onClick={handleImportButtonClick}
                    disabled={importing}
                    aria-label="Import Schedule"
                    sx={importIconButtonStyles}
                  >
                    {importing ? (
                      <CircularProgress size={18} sx={{ color: '#ffc400' }} />
                    ) : (
                      <FileUploadOutlinedIcon sx={{ fontSize: '1.65rem' }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>

              <Button
                variant="contained"
                color="inherit"
                onClick={openAddModal}
                sx={addButtonStyles}
                aria-label="Add Schedule"
                startIcon={<AddIcon sx={{ color: '#ffc400' }} />}
              >
                Add Schedule
              </Button>
            </Box>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              borderRadius: '15px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
            }}
          >
            <Table aria-label="Semester schedule table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>Time</TableCell>
                  <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>Laboratory</TableCell>
                  <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>Faculty</TableCell>
                  <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>Course</TableCell>
                  <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                    Year Level
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                    Section
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                    Subject
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                    Status
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center" />
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredSchedules.length === 0 ? (
                  renderEmptyStateRow(
                    searchTerm.trim()
                      ? 'No schedule matched your search.'
                      : `There are no schedules for ${currentDay.label} yet.`,
                    9
                  )
                ) : (
                  paginatedSchedules.map((schedule) => (
                    <TableRow key={schedule.id}>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        {formatScheduleTime(schedule.startTime, schedule.endTime)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        {schedule.selectedLab || '-'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        {schedule.facultyName || '-'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        {schedule.course || '-'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        {schedule.yearLevel || '-'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        {schedule.section || '-'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        {schedule.subject || '-'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        <Chip
                          label={schedule.active === false ? 'Inactive' : 'Active'}
                          size="small"
                          sx={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 700,
                            color:
                              schedule.active === false ? '#991b1b' : '#065f46',
                            bgcolor:
                              schedule.active === false ? '#fee2e2' : '#d1fae5',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        <EditIcon
                          sx={{
                            color: '#130f72',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': { color: '#0f0c5c' },
                            '&:active': { color: '#0b0947' },
                          }}
                          onClick={() => handleEdit(schedule.id)}
                          aria-label="Edit schedule"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              rowsPerPageOptions={[7]}
              component="div"
              count={filteredSchedules.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              sx={{
                fontFamily: 'Poppins, sans-serif',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontFamily: 'Poppins, sans-serif',
                  color: 'grey.400',
                },
                '& .MuiTablePagination-actions': {
                  '& .MuiIconButton-root': {
                    color: '#130f72',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: '#0f0c5c',
                    },
                    '&.Mui-disabled': {
                      color: 'grey.400',
                    },
                  },
                },
                '& .MuiSvgIcon-root': {
                  color: 'inherit',
                },
              }}
            />
          </TableContainer>
        </Paper>

        <Modal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            resetForm();
          }}
          closeAfterTransition
          disableEscapeKeyDown
          aria-labelledby="form-modal-title"
          aria-describedby="form-modal-description"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: { xs: '95%', md: '980px' },
              height: { xs: '90vh', md: '620px' },
              bgcolor: '#f4f7fe',
              borderRadius: '25px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: { xs: '100%', md: '25%' },
                display: { xs: 'none', md: 'block' },
                backgroundImage: `url(${SignInImg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderBottomLeftRadius: '25px',
              }}
            />

            <Box
              sx={{
                width: { xs: '100%', md: '75%' },
                p: { xs: 2, md: 3 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
                bgcolor: '#f8fafc',
                overflowY: 'auto',
              }}
            >
              {renderForm()}
            </Box>
          </Box>
        </Modal>

        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
          <DialogTitle
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontWeight: 700,
              color: '#130f72',
            }}
          >
            Remove Item
          </DialogTitle>

          <DialogContent>
            <DialogContentText
              sx={{
                fontFamily: 'Poppins, sans-serif',
                marginX: 5,
              }}
            >
              Are you sure you want to remove this schedule?
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setDeleteOpen(false)}
              sx={{
                fontFamily: 'Poppins, sans-serif',
                color: '#130f72',
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleDelete}
              autoFocus
              color="error"
              disabled={loading}
              sx={{
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Semester;